<template>
  <BaseModal
    v-model="open"
    :title="tituloModal"
    ok-label="Guardar"
    cancel-label="Cancelar"
    :loading="saving"
    layout="modal"
    placement="bottom"
    sheetHeight="60vh"
    @confirm="onConfirm"
    @cancel="onCancel"
  >
    <form @submit.prevent="onConfirm" class="d-flex flex-column gap-3">
      <!-- Día + Estado -->
      <div class="row g-3 align-items-end">
        <div class="col-md-7">
          <label class="form-label"><b>Día a configurar</b></label>
          <div class="dias-toggle-wrap">
            <div class="btn-group dias-toggle">
              <button
                v-for="d in DIAS"
                :key="d.value"
                type="button"
                class="btn"
                :class="form.dia === d.value ? 'btn-dia-activo' : 'btn-dia'"
                @click="form.dia = d.value"
              >
                {{ d.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Copiar a -->
        <div class="col-md-3">
          <label class="form-label"><b>Copiar configuración a</b></label>
          <div class="d-flex gap-2">
            <select class="form-select" v-model.number="diaDestino">
              <option v-for="d in DIAS" :key="d.value" :value="d.value">
                {{ d.label }}
              </option>
            </select>
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="copiarConfiguracionDia"
              :disabled="diaDestino === form.dia"
              title="Copiar configuración del día actual al día seleccionado"
            >
              <span class="material-symbols-rounded" aria-hidden="true">
                content_copy
              </span>
            </button>
          </div>
        </div>

        <!-- Estado del día -->
        <div class="col-md-2">
          <label class="form-label d-block mb-1">Estado del día</label>

          <div class="d-flex align-items-center gap-3 flex-wrap estado-dia">
            <!-- Solo el switch -->
            <div class="form-check form-switch mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                id="switch-estado-dia"
                v-model="diaState.abierto"
                :true-value="true"
                :false-value="false"
              />
            </div>

            <!-- Pill a la derecha del check -->
            <div
              class="estado-pill d-inline-flex align-items-center px-3 py-1 rounded-pill"
              :class="
                diaState.abierto ? 'estado-pill-open' : 'estado-pill-closed'
              "
            >
              <span class="material-symbols-rounded me-1" aria-hidden="true">
                {{ diaState.abierto ? "event_available" : "event_busy" }}
              </span>
              <small>
                {{
                  diaState.abierto
                    ? "Disponible para reservas"
                    : "Día disponible para carga"
                }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Cerrado -->
      <div v-if="diaState.abierto === false" class="alert alert-info mb-0">
        <SinDatos mensaje="Cancha cerrada para el día seleccionado" />
      </div>

      <div v-else class="row g-3">
        <!-- IZQUIERDA: Horarios -->
        <div class="col-md-6">
          <div class="card h-100">
            <div
              class="card-header d-flex align-items-center justify-content-between"
            >
              <strong>Horarios</strong>
              <button
                type="button"
                class="btn btn-sm btn-success"
                @click="agregarHorario"
              >
                Agregar horario
              </button>
            </div>
            <div class="card-body p-0 d-flex flex-column">
              <div v-if="diaState.horarios.length === 0" class="p-3">
                <em
                  >No hay horarios. Agregá la primera con “Agregar horario”.</em
                >
              </div>

              <div v-else class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 160px">Desde</th>
                      <th style="width: 160px">Hasta</th>
                      <th style="width: 60px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(f, idx) in diaState.horarios" :key="idx">
                      <td>
                        <select class="form-select" v-model="f.desde">
                          <option v-for="opt in HORAS" :key="opt" :value="opt">
                            {{ opt }}
                          </option>
                        </select>
                      </td>
                      <td>
                        <select class="form-select" v-model="f.hasta">
                          <option v-for="opt in HORAS" :key="opt" :value="opt">
                            {{ opt }}
                          </option>
                        </select>
                      </td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          @click="eliminarHorario(idx)"
                        >
                          <span
                            class="material-symbols-rounded"
                            aria-hidden="true"
                            >delete</span
                          >
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="px-3 py-2 small text-muted">
                Sugerencia: evitá solapamientos y asegurate que “Hasta” sea
                mayor a “Desde”.
              </div>
            </div>
          </div>
        </div>

        <!-- DERECHA: Precios por horario -->
        <div class="col-md-6">
          <div class="card h-100">
            <div
              class="card-header d-flex align-items-center justify-content-between"
            >
              <strong>Precios por horario por hora</strong>
              <button
                type="button"
                class="btn btn-sm btn-success"
                @click="agregarPrecio"
              >
                Agregar precio
              </button>
            </div>

            <div class="card-body p-0 d-flex flex-column">
              <div v-if="diaState.precios.length === 0" class="p-3">
                <em
                  >Definí reglas por horario (p. ej. 18:00–23:00 a $25000).</em
                >
              </div>

              <div v-else class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 120px">Desde</th>
                      <th style="width: 120px">Hasta</th>
                      <th style="width: 170px">Cliente</th>
                      <th style="width: 140px">Precio</th>
                      <th style="width: 60px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, idx) in diaState.precios" :key="idx">
                      <td>
                        <select class="form-select" v-model="r.desde">
                          <option v-for="opt in HORAS" :key="opt" :value="opt">
                            {{ opt }}
                          </option>
                        </select>
                      </td>
                      <td>
                        <select class="form-select" v-model="r.hasta">
                          <option v-for="opt in HORAS" :key="opt" :value="opt">
                            {{ opt }}
                          </option>
                        </select>
                      </td>
                      <td>
                        <select
                          class="form-select"
                          v-model.number="r.tipoCliente"
                        >
                          <option
                            v-for="t in tipo_cliente"
                            :key="t.id_tipo_cliente"
                            :value="t.id_tipo_cliente"
                          >
                            {{ t.descripcion }}
                          </option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          class="form-control text-end"
                          min="0"
                          step="1000"
                          v-model.number="r.precio"
                          placeholder="10000"
                        />
                      </td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          @click="eliminarPrecio(idx)"
                        >
                          <span
                            class="material-symbols-rounded"
                            aria-hidden="true"
                            >delete</span
                          >
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="px-3 py-2 small text-muted">
                Estas reglas se aplican por rango horario y tipo de cliente.
                Evitá solapamientos.
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </BaseModal>

  <!-- Mensaje de validación -->
  <MostrarMensaje
    v-model:modelValue="validacion.visible"
    status="error"
    :message="validacion.mensaje"
    slug="error-horarios"
    :duration="4000"
    :closable="true"
  />
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from "vue";
import BaseModal from "@/components/BaseModal.vue";
import MostrarMensaje from "./MostrarMensaje.vue";
import SinDatos from "./SinDatos.vue";
import {
  apiGetObtenerCanchaHorarioPrecio,
  apiGetTipoCliente,
} from "@/api";

/* Props / Emits */
const props = defineProps({
  modelValue: { type: Boolean, required: true },
  sport: { type: Object, required: true },
  cancha: { type: Object, default: () => ({}) },
});
const emit = defineEmits(["update:modelValue", "saved", "cancel"]);

/* Estado modal (v-model) */
const open = ref(props.modelValue);
watch(open, (v) => emit("update:modelValue", v));

watch(
  () => props.modelValue,
  async (v) => {
    open.value = v;
    if (v) {
      await inicializarFormulario();
    }
  },
);

/* Constantes */
const DIAS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 7, label: "Domingo" },
  //{ value: 8, label: "Feriado" },
];

// half-hours 00/30
const HORAS = computed(() => {
  const division = Number(props.sport?.minDivisionTurno ?? 5);

  const horas = [];

  for (let minutoTotal = 0; minutoTotal < 24 * 60; minutoTotal += division) {
    const hora = Math.floor(minutoTotal / 60);
    const minuto = minutoTotal % 60;

    horas.push(
      `${String(hora).padStart(2, "0")}:${String(minuto).padStart(2, "0")}`,
    );
  }

  return horas;
});

/* Formulario */
const tituloModal = ref("Días y Horarios");
const saving = ref(false);
const validacion = reactive({ visible: false, mensaje: "" });

const form = reactive({
  dia: 1,
});

const diaDestino = ref(1);
const tipo_cliente = ref([]);

// acá guardamos lo que el usuario edita, por día
const diasData = reactive({
  // [1..8]: { abierto: true/false, horarios: [...], precios: [...] }
});

function descTipoCliente(id) {
  return (
    tipo_cliente.value.find((t) => t.id_tipo_cliente === id)?.descripcion ??
    `tipo ${id}`
  );
}

// crea el contenedor del día si no existe
function ensureDia(dia, abiertoPorDefecto = false) {
  if (!diasData[dia]) {
    diasData[dia] = {
      abierto: abiertoPorDefecto,
      horarios: [], // [{ id, desde, hasta }]
      precios: [], // [{ id, desde, hasta, precio, tipoCliente }]
    };
  }
}

watch(
  () => form.dia,
  (d) => ensureDia(d),
  { immediate: true },
);

const diaState = computed(() => diasData[form.dia]);

/* Carga desde la DB */
async function cargarConfiguracionDesdeDB() {
  // inicializar todos los días como cerrados
  const todosLosDias = DIAS.map((d) => d.value);
  for (const d of todosLosDias) {
    diasData[d] = {
      abierto: false,
      horarios: [],
      precios: [],
    };
  }

  const idCancha = props.cancha?.id_cancha ?? null;
  if (!idCancha) {
    return;
  }

  try {
    const resp = await apiGetTipoCliente();
    tipo_cliente.value = resp;
  } catch (err) {
    console.error("Error cargando los tipos de clientes");
    return;
  }

  try {
    const resp = await apiGetObtenerCanchaHorarioPrecio({ idCancha });
    const arr = resp?.configuracionPorDia ?? [];

    for (const item of arr) {
      const d = Number(item.dia);
      if (!d) continue;

      diasData[d] = {
        abierto: !!item.abierto,
        horarios: (item.horarios ?? []).map((f) => ({
          id: f.id ?? null,
          desde: normalizarHora(f.desde),
          hasta: normalizarHora(f.hasta),
        })),
        precios: (item.preciosHorario ?? [])
          .map((r) => ({
            id: r.id ?? null,
            desde: normalizarHora(r.desde),
            hasta: normalizarHora(r.hasta),
            precio: Number(r.precio ?? 0),
            tipoCliente: r.tipoCliente,
          }))
          .sort((a, b) => {
            const clienteA = Number(a.tipoCliente ?? 0);
            const clienteB = Number(b.tipoCliente ?? 0);

            if (clienteA !== clienteB) {
              return clienteA - clienteB;
            }

            return toMin(a.desde) - toMin(b.desde);
          }),
      };
    }
  } catch (err) {
    console.error("Error cargando configuración días/horarios:", err);
  }
}


/* Inicialización al abrir */
async function inicializarFormulario() {
  tituloModal.value = props.cancha?.descripcion
    ? `Días/Horarios · ${props.cancha.descripcion}`
    : "Días/Horarios";

  form.dia = 1;
  diaDestino.value = 1;

  await cargarConfiguracionDesdeDB();

  ensureDia(form.dia);
}

function copiarConfiguracionDia() {
  const origen = form.dia;
  const destino = diaDestino.value;

  if (!origen || !destino || origen === destino) return;

  ensureDia(origen);
  ensureDia(destino);

  const src = diasData[origen];
  const dest = diasData[destino];

  dest.abierto = src.abierto;

  dest.horarios = src.horarios.map((h) => ({
    id: null,
    desde: h.desde,
    hasta: h.hasta,
  }));

  dest.precios = src.precios.map((p) => ({
    id: null,
    desde: p.desde,
    hasta: p.hasta,
    precio: p.precio,
    tipoCliente: p.tipoCliente ?? null,
  }));
}

/* Helpers */
function agregarHorario() {
  diaState.value.horarios.push({
    id: null,
    desde: "08:00",
    hasta: "23:00",
  });
}

function eliminarHorario(idx) {
  diaState.value.horarios.splice(idx, 1);
}

function agregarPrecio() {
  diaState.value.precios.push({
    id: null,
    desde: "08:00",
    hasta: "18:00",
    precio: 25000,
    tipoCliente: 1,
  });
}

function eliminarPrecio(idx) {
  diaState.value.precios.splice(idx, 1);
}

function toMin(HHmm) {
  const [h, m] = HHmm.split(":").map(Number);
  return h * 60 + m;
}

function validarDia(d, data) {
  const nombreDia = DIAS.find((x) => x.value === d)?.label ?? `Día ${d}`;

  if (data.abierto === true && data.horarios.length === 0) {
    return `(${nombreDia}) Agregá al menos un horario o marcá el día como cerrado.`;
  }

  // === HORARIOS ===
  for (const f of data.horarios) {
    if (!f.desde || !f.hasta) {
      return `(${nombreDia}) Completá “Desde” y “Hasta” en todos los horarios.`;
    }
    if (toMin(f.hasta) <= toMin(f.desde)) {
      return `(${nombreDia}) Cada horario debe tener “Hasta” mayor que “Desde”.`;
    }
  }

  const horariosOrd = [...data.horarios].sort(
    (a, b) => toMin(a.desde) - toMin(b.desde),
  );
  for (let i = 1; i < horariosOrd.length; i++) {
    const prev = horariosOrd[i - 1];
    const curr = horariosOrd[i];
    if (toMin(curr.desde) < toMin(prev.hasta)) {
      return `(${nombreDia}) Horarios: hay solapamiento entre ${prev.desde}-${prev.hasta} y ${curr.desde}-${curr.hasta}.`;
    }
  }

  // === REGLAS DE PRECIOS (con tipo de cliente) ===
  for (const r of data.precios) {
    if (!r.desde || !r.hasta) {
      return `(${nombreDia}) Completá “Desde” y “Hasta” en todas las reglas de precio.`;
    }
    if (toMin(r.hasta) <= toMin(r.desde)) {
      return `(${nombreDia}) Cada regla de precio debe tener “Hasta” mayor que “Desde”.`;
    }
    if (r.precio == null || isNaN(r.precio) || r.precio < 0) {
      return `(${nombreDia}) El precio de la regla debe ser un número válido (>= 0).`;
    }
    if (r.tipoCliente == null || r.tipoCliente === "") {
      return `(${nombreDia}) Seleccioná el tipo de cliente en todas las reglas de precio.`;
    }
  }

  // 2.a) Cada precio debe estar contenido en algún horario de la cancha
  for (const r of data.precios) {
    const desdeP = toMin(r.desde);
    const hastaP = toMin(r.hasta);

    const encajaEnAlgunHorario = data.horarios.some((h) => {
      const desdeH = toMin(h.desde);
      const hastaH = toMin(h.hasta);
      return desdeP >= desdeH && hastaP <= hastaH;
    });

    if (!encajaEnAlgunHorario) {
      const cli = descTipoCliente(r.tipoCliente);
      return `(${nombreDia}) Precios: el rango ${r.desde}-${r.hasta} para el tipo de cliente "${cli}" está fuera del horario configurado de la cancha.`;
    }
  }

  // 2.b) No debe haber solapamientos de precios para el mismo tipo de cliente
  const preciosOrd = [...data.precios].sort(
    (a, b) => toMin(a.desde) - toMin(b.desde),
  );
  for (let i = 1; i < preciosOrd.length; i++) {
    const prev = preciosOrd[i - 1];
    const curr = preciosOrd[i];

    if (
      curr.tipoCliente === prev.tipoCliente &&
      toMin(curr.desde) < toMin(prev.hasta)
    ) {
      const cli = descTipoCliente(curr.tipoCliente);
      return `(${nombreDia}) Precios: hay solapamiento entre ${prev.desde}-${prev.hasta} y ${curr.desde}-${curr.hasta} para ${cli}.`;
    }
  }

  return null;
}

function validarTodo() {
  for (const dStr of Object.keys(diasData)) {
    const d = Number(dStr);
    const err = validarDia(d, diasData[d]);
    if (err) return err;
  }
  return null;
}

async function onConfirm() {
  const error = validarTodo();
  if (error) {
    validacion.mensaje = error;
    validacion.visible = true;
    return;
  }

  saving.value = true;
  try {
    const idsHorariosUsadas = [];
    const idsPreciosUsadas = [];

    const visible = {
      dia: form.dia,
      abierto: !!diasData[form.dia].abierto,
      horarios: diasData[form.dia].abierto
        ? diasData[form.dia].horarios.map(({ id, desde, hasta }) => {
            if (id != null) idsHorariosUsadas.push(id);
            return { id, desde, hasta };
          })
        : [],
      preciosHorario: diasData[form.dia].abierto
        ? diasData[form.dia].precios.map(
            ({ id, desde, hasta, precio, tipoCliente }) => {
              if (id != null) idsPreciosUsadas.push(id);
              return {
                id,
                desde,
                hasta,
                precio: Number(precio),
                // ajustá el nombre del campo para tu backend
                tipoCliente: tipoCliente != null ? Number(tipoCliente) : null,
              };
            },
          )
        : [],
    };

    const todosLosDias = Object.entries(diasData).map(([d, data]) => {
      const diaNum = Number(d);

      const horarios = data.abierto
        ? data.horarios.map(({ id, desde, hasta }) => {
            if (id != null) idsHorariosUsadas.push(id);
            return { id, desde, hasta };
          })
        : [];

      const preciosHorario = data.abierto
        ? data.precios.map(({ id, desde, hasta, precio, tipoCliente }) => {
            if (id != null) idsPreciosUsadas.push(id);
            return {
              id,
              desde,
              hasta,
              precio: Number(precio),
              tipoCliente: tipoCliente != null ? Number(tipoCliente) : null,
            };
          })
        : [];

      return {
        dia: diaNum,
        abierto: !!data.abierto,
        horarios,
        preciosHorario,
      };
    });

    const idsHorariosUnicos = [...new Set(idsHorariosUsadas)];
    const idsPreciosUnicos = [...new Set(idsPreciosUsadas)];

    const payload = {
      idDeporte: props.sport?.id ?? null,
      idCancha: props.cancha?.id_cancha ?? null,
      diaActual: visible,
      configuracionPorDia: todosLosDias,
      idsEnUso: {
        horarios: idsHorariosUnicos,
        precios: idsPreciosUnicos,
      },
    };

    emit("saved", payload);
    open.value = false;
  } catch (e) {
    console.error(e);
    validacion.mensaje = "Ocurrió un error al guardar.";
    validacion.visible = true;
    function descTipoCliente(id) {
      return (
        tipo_cliente.value.find((t) => t.id_tipo_cliente === id)?.descripcion ??
        `tipo ${id}`
      );
    }
  } finally {
    saving.value = false;
  }
}

function onCancel() {
  emit("cancel");
  open.value = false;
}

onMounted(async () => {
  if (props.modelValue) {
    await inicializarFormulario();
  }
});

function normalizarHora(valor) {
  if (valor == null || valor === "") return "";

  const texto = String(valor).trim();

  // PostgreSQL TIME:
  // "08:00"
  // "08:00:00"
  // "08:00:00.000000"
  const horaMatch = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(texto);

  if (horaMatch) {
    const hh = String(horaMatch[1]).padStart(2, "0");
    const mm = horaMatch[2];

    return `${hh}:${mm}`;
  }

  // Formatos datetime:
  // "1900-01-01T08:00:00"
  // "1900-01-01 08:00:00"
  const fechaHoraMatch = /^\d{4}-\d{2}-\d{2}[T ](\d{1,2}):(\d{2})/.exec(texto);

  if (fechaHoraMatch) {
    const hh = String(fechaHoraMatch[1]).padStart(2, "0");
    const mm = fechaHoraMatch[2];

    return `${hh}:${mm}`;
  }

  return texto;
}

const dirtyDias = ref(new Set());

const marcarDiaDirty = (dia) => {
  dirtyDias.value.add(dia);
};

// Si necesitás que Vue reaccione:
const dirtyDiasVersion = ref(0);
const marcarDiaDirtyReactive = (dia) => {
  dirtyDias.value.add(dia);
  dirtyDiasVersion.value++;
};

const buildPayloadParcial = () => {
  const dias = Array.from(dirtyDias.value);

  return {
    idCancha: idCanchaSeleccionada.value,
    diasModificados: configuracionPorDia.value
      .filter((d) => dias.includes(d.dia))
      .map((d) => ({
        dia: d.dia,
        abierto: !!d.abierto,
        horarios: (d.horarios || []).map((h) => ({
          id: h.id ?? null,
          desde: h.desde,
          hasta: h.hasta,
        })),
        preciosHorario: (d.preciosHorario || []).map((p) => ({
          id: p.id ?? null,
          desde: p.desde,
          hasta: p.hasta,
          precio: p.precio,
          tipoCliente: p.tipoCliente,
        })),
      })),
  };
};
</script>

<style scoped>
:global(.fo-backdrop.place-bottom .as-modal) {
  display: flex;
  flex-direction: column;
}
:global(.fo-backdrop.place-bottom .as-modal .card-body) {
  flex: 1 1 auto;
  overflow: auto;
}

.table td,
.table th {
  vertical-align: middle;
}

.estado-dia .form-check-input {
  cursor: pointer;
}

/* Pill de estado */
.estado-pill {
  font-size: 0.8rem;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease, box-shadow 0.15s ease;
}

.estado-pill-open {
  background-color: #e6f6ec;
  color: #137333;
  border-color: #b7e1cd;
  box-shadow: 0 0 0 0.05rem rgba(19, 115, 51, 0.2);
}

.estado-pill-closed {
  background-color: #f1f3f4;
  color: #5f6368;
  border-color: #dadce0;
}

.estado-pill .material-symbols-rounded {
  font-size: 1.1rem;
  line-height: 1;
}

.dias-toggle .btn-dia {
  background: #fff;
  border: 1px solid #ccc;
  color: #333;
  font-weight: 500;
}

.dias-toggle .btn-dia:hover {
  background: #f5f5f5;
}

.dias-toggle .btn-dia-activo {
  background: #28a745 !important;
  color: white !important;
  border: 1px solid #1e7e34 !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
  font-weight: 600;
}

.dias-toggle-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.dias-toggle {
  width: max-content;
  min-width: 100%;
  flex-wrap: nowrap;
}

.dias-toggle .btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .dias-toggle-wrap {
    padding-bottom: 0.25rem;
  }

  .dias-toggle {
    min-width: max-content;
  }
}
</style>
